import { isSpliceSearchResult, type SpliceSearchResult } from "@/shared/splice-search.api";

/**
 * How a catalog sample travels from the Sounds browser panel to a dropzone on
 * the web.
 *
 * In the plugin the host owns this: the Sounds browser starts a native drag,
 * tells us which assets are in it (`assetsDragStarted`), and the OS delivers
 * the file. On the web there is no host and no file on disk, so the drag
 * carries the search result itself under a private MIME type, and the drop
 * side fetches the preview and stores it with the catalog metadata attached.
 *
 * The result rides the drag rather than an id because a dropzone should not
 * need to know where the drag came from to accept it: everything needed to
 * turn the drop into a resource (preview URL, name, BPM, key, tags, artwork)
 * is in the payload. A `File` is added alongside it when the browser already
 * has the preview in hand, so targets that only read `dataTransfer.files`
 * still get audio — without the metadata, which is why targets that know
 * about this payload read it first.
 */
export const kSampleDragMimeType = "application/x-splice-labs-sample";

export const writeSampleDragPayload = (
    dataTransfer: DataTransfer,
    result: SpliceSearchResult,
    file: File | undefined,
): void => {
    dataTransfer.setData(kSampleDragMimeType, JSON.stringify(result));
    // `text/plain` so a drop into a text field or another app pastes the
    // sample's name rather than nothing.
    dataTransfer.setData("text/plain", result.metadata.name);
    if (file !== undefined) {
        try {
            dataTransfer.items.add(file);
        } catch {
            // Some engines refuse programmatic files on a drag. The payload
            // still carries the result, so the drop side fetches instead.
        }
    }
    dataTransfer.effectAllowed = "copy";
};

export const hasSampleDragPayload = (types: readonly string[]): boolean =>
    types.includes(kSampleDragMimeType);

export const parseSampleDragPayload = (raw: string): SpliceSearchResult | undefined => {
    if (raw === "") {
        return undefined;
    }
    try {
        const parsed: unknown = JSON.parse(raw);
        return isSpliceSearchResult(parsed) ? parsed : undefined;
    } catch {
        return undefined;
    }
};

export const readSampleDragPayload = (
    dataTransfer: DataTransfer | null,
): SpliceSearchResult | undefined => {
    if (dataTransfer === null) {
        return undefined;
    }
    return parseSampleDragPayload(dataTransfer.getData(kSampleDragMimeType));
};
